import { Link } from "react-router-dom";
import { FileQuestion, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { useMyAssignments } from "@/hooks/useTests";
import { formatDate } from "@/lib/format";

/**
 * "Test from Mom -- due Sunday" -- surfaces only when there's a pending
 * (non-completed) assignment. Renders nothing otherwise so it never crowds
 * the dashboard for students with no assigned or self-serve tests. A
 * self-serve test (Phase 7) auto-assigns before generation finishes, so this
 * also handles the brief GENERATING state gracefully instead of showing a
 * broken-looking "waiting" card.
 */
export function TestAssignmentCard() {
  const assignments = useMyAssignments();
  const pending = (assignments.data ?? []).find((a) => a.status !== "COMPLETED");
  if (!pending) return null;
  const isGenerating = pending.test_status === "GENERATING";

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
    >
      <Link
        to="/tests"
        className="block rounded-2xl border border-brand/30 bg-brand-soft p-4 sm:p-5 hover:border-brand/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-brand text-white flex-shrink-0">
            {isGenerating ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileQuestion className="h-5 w-5" />}
          </div>
          <div className="min-w-0">
            <p className="font-medium text-sm">
              {isGenerating ? "Your practice test is generating..." : `${pending.subject_name ?? "A test"} is waiting`}
            </p>
            <p className="text-xs text-text-muted">
              {isGenerating
                ? "Ready in a moment"
                : `${pending.due_at ? `Due ${formatDate(pending.due_at)}` : "No due date"} - ${pending.time_limit_minutes} min`}
            </p>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
